import React, { useState } from "react";
import { 
  MessageSquare, 
  Target, 
  FileText, 
  Download, 
  Cloud, 
  Zap, 
  Layers, 
  Sparkles, 
  ShieldCheck, 
  AlertTriangle,
  X
} from "lucide-react";

const PLANS = {
  starter: {
    name: "Starter",
    price: "₹99",
    priceNum: 99,
    credits: 100,
    perCredit: "₹0.99",
    badge: null,
    features: [
      { icon: "💬", text: "100 AI credits (up to 10k chars/msg)" },
      { icon: "🎯", text: "5 ATS job analyses" },
      { icon: "📄", text: "All role templates" },
      { icon: "⬇️", text: "Unlimited PDF downloads" },
      { icon: "🔒", text: "Resume saved to cloud" },
    ],
  },
  pro: {
    name: "Pro",
    price: "₹149",
    priceNum: 149,
    credits: 180,
    perCredit: "₹0.83",
    badge: "🔥 Most Popular",
    features: [
      { icon: "💬", text: "180 AI credits (up to 30k chars/msg)" },
      { icon: "🎯", text: "15 ATS job analyses" },
      { icon: "⚡", text: "AI Resume Boost (GPT)" },
      { icon: "📄", text: "All templates + premium" },
      { icon: "🔀", text: "JD Variant builder" },
      { icon: "📝", text: "AI Cover Letter generator" },
    ],
  },
  elite: {
    name: "Elite",
    price: "₹249",
    priceNum: 249,
    credits: 300,
    perCredit: "₹0.83",
    badge: "💎 Best Value",
    features: [
      { icon: "💬", text: "300 AI credits (up to 70k chars/msg)" },
      { icon: "🎯", text: "50 ATS job analyses" },
      { icon: "⚡", text: "AI Resume Boosts" },
      { icon: "📄", text: "All templates + early access" },
      { icon: "🎖️", text: "Priority support" },
      { icon: "🚀", text: "Early access to new features" },
    ],
  },
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

function FeatureIcon({ emoji, color }) {
  const iconStyle = { width: '15px', height: '15px', color: color };
  switch (emoji) {
    case "💬": return <MessageSquare style={iconStyle} />;
    case "🎯": return <Target style={iconStyle} />;
    case "📄": return <FileText style={iconStyle} />;
    case "⬇️": return <Download style={iconStyle} />;
    case "🔒": return <Cloud style={iconStyle} />;
    case "⚡": return <Zap style={iconStyle} />;
    case "🔀": return <Layers style={iconStyle} />;
    case "📝": return <Sparkles style={iconStyle} />;
    case "🎖️": return <ShieldCheck style={iconStyle} />;
    case "🚀": return <Zap style={iconStyle} />;
    default: return <span style={{ fontSize: '13px' }}>{emoji}</span>;
  }
}

function PricingModal({ isOpen, onClose, authToken, user, credits, theme = 'light', onCreditsUpdate }) {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [successPlan, setSuccessPlan] = useState(null);
  const [error, setError] = useState(null);
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [devLoading, setDevLoading] = useState(false);
  const [devSuccess, setDevSuccess] = useState(false);

  // Detect localhost / dev environment
  const isDevMode = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
     window.location.hostname === '127.0.0.1' ||
     window.location.hostname === '');

  if (!isOpen) return null;

  const isDark = theme === "dark";

  // Color tokens mapping
  const colors = {
    bg: isDark ? "#0B1220" : "#FFFFFF",
    border: isDark ? "#1E2B4A" : "#B9C2D0",
    cardBg: isDark ? "#14213D" : "#FAFAF8",
    textPrimary: isDark ? "#FAFAF8" : "#14213D",
    textSecondary: isDark ? "#B9C2D0" : "#5B6472",
    textMuted: isDark ? "#5B6472" : "#B9C2D0",
    iconColor: "#FCA311",
    featureText: isDark ? "#FAFAF8" : "#14213D",
    
    // Pro Tier highlight colors
    proBorder: "#FCA311",
    proGlow: isDark ? "rgba(252, 163, 17, 0.15)" : "rgba(252, 163, 17, 0.08)",
    
    // Button styling
    btnProBg: isDark ? "#FCA311" : "#14213D",
    btnProText: isDark ? "#14213D" : "#FAFAF8",
    btnProHoverBg: isDark ? "#E59200" : "#0B1220",
    
    btnSecBg: isDark ? "rgba(250, 250, 248, 0.06)" : "rgba(20, 33, 61, 0.04)",
    btnSecBorder: isDark ? "rgba(250, 250, 248, 0.15)" : "rgba(20, 33, 61, 0.1)",
    btnSecText: isDark ? "#FAFAF8" : "#14213D",
    btnSecHoverBg: isDark ? "rgba(250, 250, 248, 0.1)" : "rgba(20, 33, 61, 0.08)",
    btnSecHoverBorder: isDark ? "rgba(250, 250, 248, 0.3)" : "rgba(20, 33, 61, 0.2)",
    
    // Top glow line
    topGlow: isDark 
      ? "linear-gradient(90deg, transparent 0%, rgba(252,163,17,0.7) 40%, rgba(252,163,17,0.7) 60%, transparent 100%)"
      : "linear-gradient(90deg, transparent 0%, rgba(252,163,17,0.4) 40%, rgba(252,163,17,0.4) 60%, transparent 100%)",
  };

  const handleBuyPlan = async (planKey) => {
    setLoadingPlan(planKey);
    setError(null);
    setSuccessPlan(null);
    setDevSuccess(false);

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError("Failed to load payment gateway. Check your internet connection.");
      setLoadingPlan(null);
      return;
    }

    const authHeaders = {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    };

    try {
      const orderRes = await fetch("/api/payments/create-order", {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ plan: planKey }),
      });
      const orderData = await orderRes.json();

      if (!orderRes.ok) {
        setError(orderData.error || "Failed to create order.");
        setLoadingPlan(null);
        return;
      }

      const plan = PLANS[planKey];
      const options = {
        key:         orderData.key,
        amount:      orderData.amount,
        currency:    "INR",
        name:        "Resumebar.in",
        description: `${plan.credits} Credits — ${plan.name} Plan`,
        order_id:    orderData.order_id,
        image:       "/resumebar-icon-192.png",
        prefill: { name: user?.name || "", email: user?.email || "" },
        theme: { color: "#14213D" }, // Ink Navy brand theme
        modal: { ondismiss: () => setLoadingPlan(null) },
        handler: async (response) => {
          try {
            const verifyRes = await fetch("/api/payments/verify", {
              method: "POST",
              headers: authHeaders,
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_signature:  response.razorpay_signature,
                plan: planKey,
              }),
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              onCreditsUpdate(verifyData.credits, verifyData.plan);
              setSuccessPlan(planKey);
              setLoadingPlan(null);
            } else {
              setError(verifyData.error || "Payment verification failed.");
              setLoadingPlan(null);
            }
          } catch {
            setError("Verification failed. Contact support.");
            setLoadingPlan(null);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err.message || "Something went wrong.");
      setLoadingPlan(null);
    }
  };

  const planEntries = Object.entries(PLANS);

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 modal-enter">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(16px)" }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full sm:max-w-4xl rounded-t-3xl sm:rounded-2xl overflow-hidden modal-card-enter"
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          boxShadow: isDark
            ? "0 25px 50px -12px rgba(0,0,0,0.7), 0 0 40px rgba(252,163,17,0.05)"
            : "0 25px 50px -12px rgba(0,0,0,0.12), 0 0 40px rgba(252,163,17,0.03)",
          maxHeight: "95vh",
          overflowY: "auto",
          fontFamily: "'Inter', sans-serif",
          color: colors.textPrimary,
        }}
      >
        {/* Top Gradient Bar */}
        <div className="absolute top-0 left-0 right-0 h-px"
          style={{ background: colors.topGlow }} />

        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: colors.border }} />
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl transition-all"
          style={{ background: "transparent", color: colors.textSecondary, zIndex: 50, cursor: 'pointer' }}
          onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"; e.currentTarget.style.color = colors.textPrimary; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = colors.textSecondary; }}
        >
          <X className="w-4 h-4" />
        </button>

        {/* ── HEADER ── */}
        <div className="relative px-6 sm:px-8 pt-6 pb-2 text-center">
          {/* Credit alert pill */}
          {credits !== null && credits <= 5 && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
              style={{ 
                background: credits === 0 ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)", 
                border: `1px solid ${credits === 0 ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)"}` 
              }}>
              <AlertTriangle className="w-3.5 h-3.5" style={{ color: credits === 0 ? "#EF4444" : "#F59E0B" }} />
              <span className="text-xs font-semibold" style={{ color: credits === 0 ? "#FCA5A5" : "#FCD34D" }}>
                {credits === 0 ? "No credits left — Top up to continue" : `Only ${credits} credit${credits === 1 ? "" : "s"} remaining`}
              </span>
            </div>
          )}

          <h2 className="font-black tracking-tight" style={{ fontSize: "clamp(20px, 3.5vw, 32px)", marginTop: '8px', color: colors.textPrimary }}>
            Upgrade your plan
          </h2>

          <p className="text-xs mt-3" style={{ color: colors.textMuted }}>
            1 Chat = 1+ credits &nbsp;·&nbsp; Cover Letter = 3 credits &nbsp;·&nbsp; ATS analysis = 5 credits &nbsp;·&nbsp; PDF downloads are free
          </p>
        </div>

        {/* ── PLAN CARDS ── */}
        <div className="px-6 sm:px-8 pb-8 grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4">
          {planEntries.map(([key, plan]) => {
            const isPro = key === "pro";
            const isElite = key === "elite";
            return (
              <div
                key={key}
                onMouseEnter={() => setHoveredPlan(key)}
                onMouseLeave={() => setHoveredPlan(null)}
                className="relative rounded-2xl p-6 flex flex-col justify-between"
                style={{
                  background: colors.cardBg,
                  border: isPro 
                    ? `1px solid ${colors.proBorder}` 
                    : hoveredPlan === key ? `1px solid ${isDark ? "#3F3F46" : "#B4B4BB"}` : `1px solid ${colors.border}`,
                  boxShadow: hoveredPlan === key || isPro
                    ? isPro 
                      ? `0 10px 30px ${colors.proGlow}, 0 8px 32px rgba(0,0,0,${isDark ? '0.4' : '0.08'})`
                      : `0 8px 24px rgba(0,0,0,${isDark ? '0.3' : '0.05'})`
                    : "none",
                  transform: hoveredPlan === key ? "translateY(-4px)" : "none",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap uppercase tracking-wider"
                      style={{ 
                        background: isPro ? "linear-gradient(135deg, #14213D, #0B1220)" : "linear-gradient(135deg, #D97706, #B45309)", 
                        color: "white", 
                        boxShadow: isPro ? "0 4px 12px rgba(20,33,61,0.35)" : "0 4px 12px rgba(217,119,6,0.35)" 
                      }}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Success overlay */}
                {successPlan === key && (
                  <div className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center z-10"
                    style={{ background: isDark ? "rgba(9,9,11,0.95)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)" }}>
                    <span className="text-4xl mb-2">🎉</span>
                    <p className="font-bold text-sm" style={{ color: "#34D399" }}>Credits Added!</p>
                    <p className="text-xs mt-1 text-center px-4" style={{ color: colors.textSecondary }}>
                      {plan.credits} credits have been credited to your account.
                    </p>
                    <button onClick={onClose} className="mt-4 text-xs px-4 py-2 rounded-xl font-bold transition-all hover:scale-[1.03]"
                      style={{ background: "#14213D", color: "#FAFAF8", border: "none", boxShadow: "0 4px 12px rgba(20,33,61,0.3)" }}>
                      Start Building →
                    </button>
                  </div>
                )}

                {/* Plan header details */}
                <div>
                  <div className="mb-4">
                    <p className="text-xs font-bold uppercase tracking-widest mb-1.5"
                      style={{ color: isPro ? (isDark ? "#A78BFA" : "#7C3AED") : isElite ? "#FBBF24" : "#3B82F6" }}>
                      {plan.name}
                    </p>
                    
                    {/* Price and duration */}
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', margin: '8px 0' }}>
                      <span style={{ fontSize: '36px', fontWeight: 800, color: colors.textPrimary }}>{plan.price}</span>
                      <span style={{ fontSize: '12px', color: colors.textMuted }}>/ pack</span>
                    </div>

                    {/* Cost per credit details */}
                    <div className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full"
                      style={{ 
                        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", 
                        border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}` 
                      }}>
                      <span className="text-[10px] font-bold" style={{ color: colors.textPrimary }}>{plan.credits}</span>
                      <span className="text-[10px]" style={{ color: colors.textSecondary }}>credits total ({plan.perCredit}/cr)</span>
                    </div>
                  </div>

                  {/* Features List */}
                  <ul style={{ listStyle: 'none', padding: 0, margin: '24px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {plan.features.map((f, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', height: '18px', flexShrink: 0 }}>
                          <FeatureIcon emoji={f.icon} color={colors.iconColor} />
                        </span>
                        <span style={{ fontSize: '12px', color: colors.featureText, lineHeight: '1.5' }}>{f.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Buy Button */}
                <button
                  onClick={() => handleBuyPlan(key)}
                  disabled={loadingPlan !== null}
                  className="w-full py-3 rounded-xl text-xs font-bold transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                  style={{
                    background: isPro ? colors.btnProBg : colors.btnSecBg,
                    color: isPro ? colors.btnProText : colors.btnSecText,
                    border: isPro ? 'none' : `1px solid ${colors.btnSecBorder}`,
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: isPro ? `0 4px 12px rgba(${isDark ? '255,255,255,0.1' : '0,0,0,0.1'})` : 'none',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    if (isPro) {
                      e.currentTarget.style.background = colors.btnProHoverBg;
                    } else {
                      e.currentTarget.style.background = colors.btnSecHoverBg;
                      e.currentTarget.style.borderColor = colors.btnSecHoverBorder;
                    }
                  }}
                  onMouseLeave={e => {
                    if (isPro) {
                      e.currentTarget.style.background = colors.btnProBg;
                    } else {
                      e.currentTarget.style.background = colors.btnSecBg;
                      e.currentTarget.style.borderColor = colors.btnSecBorder;
                    }
                  }}
                >
                  {loadingPlan === key ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-3.5 h-3.5 border-2 rounded-full animate-spin"
                        style={{ borderColor: isPro ? "rgba(252,163,17,0.3)" : "rgba(20,33,61,0.3)", borderTopColor: colors.textPrimary }} />
                      Opening gateway…
                    </span>
                  ) : (
                    `Upgrade to ${plan.name}`
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* ── DEV MODE BANNER ── */}
        {isDevMode && (
          <div className="mx-6 sm:mx-8 mb-5 rounded-xl overflow-hidden"
            style={{ 
              border: isDark ? '1px solid rgba(52,211,153,0.25)' : '1px solid rgba(52,211,153,0.4)', 
              background: isDark ? 'rgba(6,78,59,0.1)' : 'rgba(52,211,153,0.08)' 
            }}>
            <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <span style={{ fontSize: 16 }}>🧪</span>
                <div>
                  <p className="text-xs font-bold" style={{ color: '#10B981' }}>Local Development Mode</p>
                  <p className="text-[10.5px]" style={{ color: isDark ? '#A7F3D0' : '#047857' }}>Add 200 free credits instantly to test features</p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setDevLoading(true);
                  setError(null);
                  try {
                    const res = await fetch('/api/payments/dev-credits', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                      },
                      body: JSON.stringify({ credits: 200 }),
                    });
                    const data = await res.json();
                    if (data.success) {
                      onCreditsUpdate(data.credits);
                      setDevSuccess(true);
                      setTimeout(onClose, 1500);
                    } else {
                      setError(data.error || 'Dev credits failed');
                    }
                  } catch (e) {
                    setError('Dev credits request failed: ' + e.message);
                  } finally {
                    setDevLoading(false);
                  }
                }}
                disabled={devLoading || devSuccess}
                className="px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all shrink-0"
                style={{
                  background: devSuccess ? 'rgba(52,211,153,0.2)' : 'rgba(52,211,153,0.15)',
                  border: '1px solid rgba(52,211,153,0.4)',
                  color: '#10B981',
                  opacity: devLoading ? 0.7 : 1,
                  cursor: 'pointer'
                }}
              >
                {devSuccess ? '✅ Credits Added!' : devLoading ? 'Processing…' : '+ 200 Free Credits'}
              </button>
            </div>
          </div>
        )}

        {/* ── ERROR ── */}
        {error && (
          <div className="mx-6 sm:mx-8 mb-5 flex items-center gap-2.5 rounded-xl px-4 py-3"
            style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "#EF4444" }} />
            <p className="text-xs" style={{ color: "#FCA5A5" }}>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingModal;
